import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { UserService } from "../../core/services/user.service";
import { LoaderService } from "../../shared/components/loader/loader.service";
import { HeaderComponent } from "../../shared/layout/app/header/header.component";
import { LeftSideNavComponent } from "../../shared/layout/app/left-menu/left-side-nav.component";
import { BaseResponse } from "../../shared/models/base-response.model";
import { AuthBllService } from "../auth/auth-bll.service";
import { ProfileBllService } from "../profile/profile-bll.service";
import { UserEventStatusRespModel } from "../profile/profile.model";
import { VirtualEventBllService } from "../virtual-event/virtual-event-bll.service";
import { HomeBllService } from "./home-bll.service";
import { ExhInviteBllService } from "../exh-invite/exh-invite-bll.service";
import { InviteDetailsModel } from "../exh-invite/exh-invite.model";
import { PavilionGroupModel, PavilionModel } from "../digital-catalogue/digital-catalogue.model";
import { DigitalCatalogueBllService } from "../digital-catalogue/digital-catalogue-bll.service";
import { EventDetailsResponseModel } from "./home.model";

/**
 * HomeComponent is the landing/dashboard component after login.
 * It loads the exhibitor's brand profile, updates header UI, and handles logout.
 */
@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  imports: [HeaderComponent, LeftSideNavComponent, CommonModule],
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  // Countdown timer variables
  days: string = "00";
  hours: string = "00";
  minutes: string = "00";
  seconds: string = "00";

  // Event status flag
  isEventStarted = false;
  private timer: any;

  // Event start and end date
  private eventStart = new Date("2025-12-10T09:00:00").getTime();

  userProfile: UserEventStatusRespModel["user_profile"] | null = null;
  previewLogo: string = "/assets/images/Default-profile-photo.png"; // String path/URL

  // User authentication status
  isAuthenticated: boolean = false;

  // Ticket assignment status
  isAssigned: boolean = false;

  @ViewChild("carousel", { static: false }) carousel!: ElementRef;
  private carouselInstance: any;

  /**
   * Indicates whether the user has access to the virtual event.
   * Can be a string representing the access level or `null` if no access is granted.
   */
  accessToVirtualEvent!: string | null;

  /**
   * Indicates whether the category field has been filled.
   *
   * - `string`: Represents the filled category value.
   * - `null`: Indicates that the category has not been filled.
   */
  hasCategoryFilled!: string | null;

  /**
   * Holds the details of attendees for the user's event tickets.
   *
   * - The type is derived from `UserEventStatusRespModel["event_my_tickets"]["event_attendee_details"]`.
   * - Can be `null` if no attendee details are available.
   */
  eventAttendeeDetails:
    | UserEventStatusRespModel["event_my_tickets"]["event_attendee_details"]
    | null = null;

  /**
   * Represents the current state value, which can be a string or null.
   * Used to track or store the state information within the component.
   */
  state!: string | null;

  // Number of pending invitations for the event
  //event_pending_invitation_count: number = 0;

  /**
   * Stores the authentication status of the user.
   *
   * - If the user has just authenticated, this property may contain a string value (e.g., a token or status message).
   * - If the user has not authenticated, this property will be `null`.
   */
  justAuthenticated!: string | null;

  /**
   * Stores the pending virtual event status.
   *
   * This property holds a string value representing the current pending virtual event,
   * or `null` if there is no pending event.
   */
  virtualEventPending!: string | null;

  /**
   * Indicates whether the user possesses a virtual pass.
   * Used to control access to features or UI elements that require a virtual pass.
   */
  hasVirtualPass: boolean = false;

  /**
   * Indicates whether a category is present or selected.
   * Used to control the display or logic based on the existence of a category.
   */
  hasCategory: boolean = false;

  refSource: string = "";
  inviteList: InviteDetailsModel[] = [];
  hidePavilionSection: boolean = false;

  /**
   * Pavilion related properties
   */
  pavilionGroups: PavilionGroupModel[] = []; // List of pavilion groups
  allPavilions: PavilionModel[] = []; // Flattened list of all pavilions
  isLoadingPavilions: boolean = false;
  @ViewChild('invitationModal', { static: true }) invitationModal!: ElementRef;

  isContestActive : boolean = false;
  isVotingActive : boolean = false;

  eventDetails: EventDetailsResponseModel | null = null;
  showBuyTicketButton: boolean = false;

  constructor(
    private router: Router, // Router to navigate between routes
    private activatedRoute: ActivatedRoute, // ActivatedRoute to access route parameters
    private bllService: HomeBllService,
    private loaderService: LoaderService,
    private userService: UserService,
    private authService: AuthBllService,
    private virtualEventBLLService: VirtualEventBllService,
    private profileBllService: ProfileBllService,
    private snackBar: MatSnackBar,
    private bll: ExhInviteBllService,
    private catalogueBllService: DigitalCatalogueBllService,
  ) { }
  /**
   * Lifecycle hook that is called after data-bound properties of a component are initialized.
   *
   * This method performs the following actions:
   * - Subscribes to authentication status and updates local state accordingly.
   * - Fetches user event status if the user is authenticated; otherwise, resets related flags.
   * - Retrieves event code from the route and initializes local storage changes if available.
   * - Loads various state values from storage (e.g., authentication flow state, category completion).
   * - Initializes a countdown timer that updates every second.
   * - Handles post-authentication flows:
   *   - If the user just authenticated and is pending for a virtual event, triggers the join event logic.
   *   - If the user completed the area of interest after being redirected, auto-registers the virtual visitor.
   *
   * @returns {Promise<void>} A promise that resolves when initialization is complete.
   */
  async ngOnInit(): Promise<void> {
    this.loadEventDetails();
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      console.log("isAuthenticated:", isAuthenticated);
      this.isAuthenticated = isAuthenticated;
    });

    if (this.isAuthenticated) {
      await this.fetchUserEventStatus();
      //Call if authenticated 
        this.getPendingInvites();
      const isPavilionPage = this.state === "catalogue/pavilion-list";
      const pending = this.virtualEventPending === "true";
      const justAuth = this.justAuthenticated === "true";
      const categoryFilled = this.hasCategoryFilled === "true";

      if (isPavilionPage && pending) {

        // Case 1: Auto-trigger immediately after login
        if (justAuth) {
          this.onJoinVirtualEvent();
          return;
        }

        // Case 2: User completed category selection after redirect
        if (categoryFilled) {
          this.authService.removeStorageItem("state");
          this.onJoinVirtualEvent();
          return;
        }
      }
    } else {
      this.accessToVirtualEvent = "false";
      this.hasVirtualPass = false;
      this.hasCategoryFilled = "false";
      this.hasCategory = false;
    }

    const eventCode = this.getEventCodeFromRoute();
    this.refSource = this.getRefSourceFromRoute();
    this.justAuthenticated =
      this.authService.getStorageValue("justAuthenticated");
    this.virtualEventPending = this.authService.getStorageValue(
      "virtualEventPending"
    );

    if (eventCode && eventCode !== "") {
      this.bllService.homeInitStorageChanges(eventCode); // Save to local storage
    } else {
      console.warn("homeInitStorageChanges not called due to missing data");
    }

    if (this.refSource && this.refSource !== "") {
      this.bllService.homeInitStorageChanges("", this.refSource);
    } else {
      this.bllService.clearRefSource();
    }

    this.state = this.authService.getStorageValue("state");
    this.hasCategoryFilled =
      this.authService.getStorageValue("hasCategoryFilled") ?? "false";
    this.accessToVirtualEvent =
      this.authService.getStorageValue("accessToVirtualEvent") ?? "false";

    // Initialize countdown timer
    this.updateCountdown();
    this.timer = setInterval(() => this.updateCountdown(), 1000);

  }

  /**
   * Lifecycle hook that is called after Angular has fully initialized a component's view.
   *
   * Subscribes to observables from the user service to update the component's state regarding
   * whether the user has a virtual pass and whether the user has filled their category.
   *
   * @remarks
   * This method is part of the Angular `AfterViewInit` lifecycle interface.
   *
   * @see {@link https://angular.io/api/core/AfterViewInit}
   */
  ngAfterViewInit(): void {
    this.userService.userHasVirtualPass$.subscribe((pass) => {
      this.hasVirtualPass = pass;
    });

    this.userService.userHasFilledUserCategory$.subscribe((filled) => {
      this.hasCategory = filled;
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);

    // Dispose carousel instance to prevent memory leaks
    if (this.carouselInstance) {
      this.carouselInstance.dispose();
    }
  }

  /**
   * Indicates whether the loader is currently shown.
   * Returns `true` if the loader service is active, otherwise `false`.
   */
  get isLoading(): boolean {
    return this.loaderService.isShown;
  }

  /**
   * Determines whether the "Get Mobile Pass" option should be displayed to the user.
   *
   * @returns {boolean} `true` if the user should see the "Get Mobile Pass" option; otherwise, `false`.
   *
   * The option is hidden if the user is authenticated and either has access to the virtual event
   * or already possesses a virtual pass. In all other cases, the option is shown.
   */
  shouldShowGetMobilePass(): boolean {
    if (this.isAuthenticated) {
      if (this.accessToVirtualEvent === "true" || this.hasVirtualPass) {
        return false;
      }
    }
    return true;
  }

  /**
   * Determines whether the "Explore Kisan" feature should be displayed on mobile devices.
   *
   * @returns {boolean} `true` if the user is authenticated and either has access to the virtual event
   *                    or possesses a virtual pass; otherwise, `false`.
   */
  shouldShowExploreKisanOnMobile(): boolean {
    if (this.isAuthenticated) {
      if (this.accessToVirtualEvent === "true" || this.hasVirtualPass) {
        return true;
      }
    }
    return false;
  }

  onBuyTickets(): void {
    if (!this.isAuthenticated) {
      this.authService.saveStorageValue("state", "reg/select-ticket");
    }

    this.router.navigate(["reg/select-ticket"]);
  }

  onViewTickets(): void {
    if (!this.isAuthenticated) {
      this.authService.saveStorageValue("state", "reg/my-tickets");
    }
    this.router.navigate(["reg/my-tickets"]);
  }

  /**
   * Handles the logic for joining a virtual event.
   *
   * - Saves relevant state information to storage.
   * - Removes the "justAuthenticated" flag if present.
   * - If the user is authenticated:
   *   - If access to the virtual event is denied:
   *     - If the user's category is not filled, navigates to the area of interest selection.
   *     - Otherwise, registers the user as a virtual visitor.
   *   - If access to the virtual event is granted:
   *     - If the user's category is not filled, navigates to the area of interest selection.
   *     - Otherwise, navigates to the virtual event.
   * - If the user is not authenticated:
   *   - Sets the "justAuthenticated" flag and navigates to the authentication initialization page.
   *
   * @returns {Promise<void>} A promise that resolves when the join process is complete.
   */
  async onJoinVirtualEvent(): Promise<void> {
    this.authService.saveStorageValue("state", "virtual-event");
    this.authService.saveStorageValue("virtualEventPending", "true");

    if (this.authService.getStorageValue("justAuthenticated") === "true") {
      this.authService.removeStorageItem("justAuthenticated");
    }

    if (this.isAuthenticated) {
      if (this.accessToVirtualEvent === "false") {
        if (this.hasCategoryFilled === "false") {
          this.goToAreaOfInterest();
          return;
        }
        this.registerVirtualVisitor();
      } else {
        if (this.hasCategoryFilled === "false") {
          this.goToAreaOfInterest();
          return;
        }
        this.goToVirtualEvent();
      }
    } else {
      this.authService.saveStorageValue("justAuthenticated", "true");

      this.router.navigate(["/auth/init"]);
    }
  }

  goToAreaOfInterest(): void {
    this.router.navigate(["reg/area-of-interest"]);
  }

  /**
   * Navigates the user to the virtual event registration page.
   *
   * If the `virtualEventPending` flag is set to `'true'` either in the component or in storage,
   * it removes both the `virtualEventPending` and `state` items from storage before navigation.
   *
   * @remarks
   * This method is typically called when the user initiates an action to join a virtual event.
   */
  goToVirtualEvent(): void {
    if (
      this.virtualEventPending === "true" ||
      this.authService.getStorageValue("virtualEventPending") === "true"
    ) {
      this.authService.removeStorageItem("virtualEventPending");
      this.authService.removeStorageItem("state");
    }
    this.router.navigate(["catalogue/pavilion-list"]);
  }

  getEventCodeFromRoute(): string {
    const eventCode =
      this.activatedRoute.snapshot.queryParamMap.get("eventcode");
    if (!eventCode) {
      console.error("Event code not found in route parameters");
      return "";
    }
    return eventCode;
  }

  getRefSourceFromRoute(): string {
    const refSource =
      this.activatedRoute.snapshot.queryParamMap.get("ref_source");
    if (!refSource) {
      console.error("Ref source not found in route parameters");
      return "";
    }
    return refSource;
  }

  /**
   * Registers a virtual visitor for the event.
   *
   * This method displays a loader while the registration request is being processed.
   * On successful registration (when the response message is "Operation succeeded"),
   * it shows a virtual pass popup. If the response message is unexpected or if an error
   * occurs during registration, it navigates the user to the registration home page and
   * displays an error alert.
   *
   * @returns {Promise<void>} A promise that resolves when the registration process is complete.
   */
  private async registerVirtualVisitor(): Promise<void> {
    this.loaderService.show();

    try {
      await this.virtualEventBLLService.submitRegisterVirtualVisitor();

      this.authService.saveStorageValue("accessToVirtualEvent", "true");
      this.userService.setHasVirtualPass(true);

      this.showVirtualPassPopup();
    } catch (error) {
      console.error("Registration failed:", error);

      this.snackBar.open("Registration failed. Please try again.", "Close", {
        duration: 4000,
      });

      this.router.navigate(["/home"], { replaceUrl: true });
    } finally {
      this.loaderService.hide();
    }
  }

  /**
   * Displays the virtual pass modal popup using Bootstrap's modal functionality.
   *
   * Attempts to find the modal element by its ID ('virtualPassModal') in the DOM.
   * If found, it creates and shows a Bootstrap modal instance.
   * If the element is not found, logs an error to the console.
   *
   * @private
   */
  private showVirtualPassPopup() {
    const modalElement = document.getElementById("virtualPassModal");
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    } else {
      console.error("Modal element not found");
    }
  }

  /**
   *
   * @returns void
   * Updates the countdown timer every second.
   * If the event has started or ended, updates the status message accordingly.
   */
  private updateCountdown(): void {
    const now = Date.now();

    // When event starts → hide timer
    if (now >= this.eventStart) {
      clearInterval(this.timer);
      this.isEventStarted = true; // hide timer
      return;
    }

    // Before event starts → show countdown
    this.isEventStarted = false;

    const diff = this.eventStart - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    this.days = String(days).padStart(2, "0");
    this.hours = String(hours).padStart(2, "0");
    this.minutes = String(minutes).padStart(2, "0");
    this.seconds = String(seconds).padStart(2, "0");
  }

  /**
   * Fetches the current user's event status and updates relevant user and event details in the component.
   *
   * - Shows a loader while fetching data.
   * - Retrieves user profile and event ticket details from the backend.
   * - Updates user profile image, name, mobile, state, and city.
   * - Sets flags for virtual pass access and category completion in local storage.
   * - Updates pending invitation count and ticket assignment status.
   * - Handles errors by logging and displaying an alert.
   * - Hides the loader after completion.
   *
   * @returns {Promise<void>} Resolves when the user event status has been fetched and processed.
   */
  async fetchUserEventStatus() {
    this.loaderService.show();
    try {
      const res: BaseResponse<UserEventStatusRespModel> =
        await this.profileBllService.submitGetUserEventStatus();
      this.userProfile = res.data.user_profile;

      this.eventAttendeeDetails =
        res.data.event_my_tickets.event_attendee_details ?? null;

      // Update pending invitation count
      // this.event_pending_invitation_count =
      //   res.data.event_my_tickets.event_pending_invitation_count ?? 0;

      if (this.userProfile) {
        if (this.userProfile.image_url) {
          this.previewLogo =
            this.userProfile.image_url ??
            "/assets/images/Default-profile-photo.png";
        }

        let userName =
          res.data.user_profile.first_name +
          " " +
          res.data.user_profile.last_name;
        this.userService.setUser({
          name: userName, // get from API or JWT
          profileImg: this.previewLogo, // fallback if empty
          mobile: res.data.user_profile.mobile,
          state: res.data.user_profile.state,
          city: res.data.user_profile.city,
          hasUnassignedTickets:
            res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
          hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
        });

        if (this.eventAttendeeDetails !== null) {
          this.userService.setHasVirtualPass(true);

          if (
            this.authService.getStorageValue("accessToVirtualEvent") ===
            "false" ||
            this.authService.getStorageValue("accessToVirtualEvent") === null
          ) {
            this.authService.saveStorageValue("accessToVirtualEvent", "true");
          }
        }

        if (
          this.userProfile.category !== null &&
          this.userProfile.category !== ""
        ) {
          this.userService.setHasFilledUserCategory(true);
          if (
            this.authService.getStorageValue("hasCategoryFilled") === "false" ||
            this.authService.getStorageValue("hasCategoryFilled") === null
          ) {
            this.authService.saveStorageValue("hasCategoryFilled", "true");
          }
        }

        const assignedForMe = res.data.event_my_tickets.tickets_assigned_for_me;
        const assigned =
          res.data.event_my_tickets.assigned_tickets_bought_by_me;
        this.isAssigned = (assignedForMe ?? 0) > 0 || (assigned ?? 0) > 0;
      }
    } catch (err) {
      console.error("Error fetching user event status:", err);
      alert("Failed to fetch user event status. Please try again.");
    } finally {
      this.loaderService.hide();
    }
  }
  getPendingInvites() {
    this.bll
      .getPendingInviteList()
      .then((response) => {
        // Validate response data structure
        this.inviteList = Array.isArray(response?.data) ? response.data : [];
        if (this.inviteList.length > 0) {
          const modalEl = this.invitationModal.nativeElement;
          const modal = this.bllService.getBootstrapModal(modalEl);
          modal.show();
        }
      })
      .catch((error) => {
        console.error("Error fetching pending invites:", error);
        // keep state consistent on error
        this.inviteList = [];
      });
  }

  onClickCatalog(): void {
    if (!this.isAuthenticated) {
      this.authService.saveStorageValue("state", "catalogue/pavilion-list");
    }
    this.router.navigate(["catalogue/pavilion-list"]);
  }

  onClickPavilion(pavilion: any): void {

    this.catalogueBllService.saveSelectedPavilionDetails(
      pavilion.code,
      pavilion.name,
      pavilion.pavilion_hover_image_url
    );

    this.router.navigate(["/catalogue/products-list"], {
      queryParams: { pavilion_code: pavilion.code },
    });
  }

  /**
   * Navigates the user to the accept invitation page.
   */
  navigateToAcceptInvite(): void {

    const modalEl = this.invitationModal.nativeElement;
    const bsModal = this.bllService.getBootstrapModal(modalEl);

    bsModal.hide();

    // guard: no invites loaded or list is empty
    if (!this.inviteList || this.inviteList.length === 0) {
      return;
    }
    if (this.inviteList.length === 1) {
      // navigate to invite detail page
      const code = this.inviteList[0]?.encoded_invite_code;
      if (!code) {
        return;
      }
      this.router.navigate(["/exh-invite/details"], {
        queryParams: { code },
      });
    } else {
      this.router.navigate(["/exh-invite/list"]);
    }

  }

  /**
  * Fetches the pavilion list from the BLL service.
  */
  callfetchPavilionList() {
    this.isLoadingPavilions = true;
    this.catalogueBllService
      .fetchPavilionList()
      .then((response) => {
        this.isLoadingPavilions = false;
        this.pavilionGroups = response.data.pavilion_groups;

        // Flatten all pavilion groups into one unified list
        this.allPavilions = this.pavilionGroups.flatMap(
          (group) => group.pavilions
        );

        this.hidePavilionSection = false;
      })
      .catch((error) => {
        this.isLoadingPavilions = false;
        console.error("Error fetching pavilion data:", error);
        if (error.status === 410) {
          // hide pavilion section
          this.hidePavilionSection = true;
        }
      });
  }

  viewLayout() {
    window.open('https://kisan-common.s3.ap-south-1.amazonaws.com/event/kisan25/kisan2025_layout.pdf', '_self');
  }

  onClickVote(): void {
    this.router.navigate(['contests/participants-list']);
  }

  loadEventDetails() {
    this.bllService.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;     
        this.isContestActive = this.eventDetails.is_contest_active;
        this.isVotingActive = this.eventDetails.is_voting_active;

        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });
        
        if (this.eventDetails.allow_to_register === 1 && this.eventDetails.event_status !== 'over') {
          this.showBuyTicketButton = true;
        } else {
          this.showBuyTicketButton = false;
        }

        if(this.eventDetails.show_digital_catalog) {
          this.callfetchPavilionList();
        }
      })
      .catch((error) => {
        this.showBuyTicketButton = false;
        console.error('Error fetching event details:', error);
      });
  }

}
